/**
 * Immutable audit trail for leave adjustments — read-only view.
 *
 * There is deliberately no edit and no delete here: the table refuses both at
 * the database level (a BEFORE UPDATE OR DELETE trigger), the API exposes no
 * such route, and this component only ever GETs. A correction appears as a
 * REVERSE row pointing at the entry it undoes.
 *
 * Timestamps arrive as ISO-8601 UTC together with the tenant's IANA timezone,
 * and are rendered in that zone — not the browser's. An HR officer reviewing
 * the trail from another country must see the same wall-clock time the operator
 * saw, otherwise "who changed what at 23:40" becomes unanswerable.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Stack, Typography, TextField, MenuItem, Select, FormControl,
  InputLabel, Chip, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, TablePagination, Tooltip, IconButton, Alert, Skeleton,
  LinearProgress, Collapse, Button,
} from '@mui/material';
import RefreshRoundedIcon    from '@mui/icons-material/RefreshRounded';
import LockRoundedIcon       from '@mui/icons-material/LockRounded';
import UndoRoundedIcon       from '@mui/icons-material/UndoRounded';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon   from '@mui/icons-material/KeyboardArrowUp';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import { authFetch } from '../../services/apiServices';
import WorkerPicker from '../common/WorkerPicker';

const fmtDays = (n) => {
  const r = Math.round((Number(n) || 0) * 100) / 100;
  return r.toFixed(2).replace(/\.?0+$/, '');
};
export const fmtSigned = (n) => ((Number(n) || 0) > 0 ? `+${fmtDays(n)}` : fmtDays(n));

/**
 * Render a stored UTC instant in the tenant's timezone.
 * Falls back to the browser zone only when the tenant has none configured —
 * and says so, rather than silently showing a different clock.
 */
export function formatInTenantTz(utcIso, timeZone) {
  if (!utcIso) return '—';
  const d = new Date(utcIso);
  if (Number.isNaN(d.getTime())) return '—';
  try {
    return new Intl.DateTimeFormat('sq-AL', {
      timeZone: timeZone || undefined,
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      // 24-hour, always. sq-AL defaults to 12-hour with a localised
      // "e pasdites" marker — an audit timestamp must not be ambiguous, and
      // must not change shape with the viewer's locale.
      hour12: false, hourCycle: 'h23',
    }).format(d);
  } catch {
    // An unknown IANA zone must not blank the whole trail.
    return d.toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
  }
}

const YEARS = (() => { const y = new Date().getFullYear(); return [y + 1, y, y - 1, y - 2, y - 3]; })();

export default function AuditTrailPanel({ workers = [], adjustmentId = null }) {
  const [rows, setRows]     = useState([]);
  const [total, setTotal]   = useState(0);
  const [tz, setTz]         = useState('');
  const [tenant, setTenant] = useState('');
  const [loading, setLoad]  = useState(true);
  const [error, setError]   = useState('');

  const [fWorker, setFWorker] = useState('');
  const [fActor, setFActor]   = useState('');
  const [fAction, setFAction] = useState('');
  const [fEvent, setFEvent]   = useState('');
  const [fYear, setFYear]     = useState('');
  const [from, setFrom]       = useState('');
  const [to, setTo]           = useState('');
  const [page, setPage]       = useState(0);
  const [rpp, setRpp]         = useState(50);
  const [openRow, setOpenRow] = useState(null);

  const load = useCallback(async () => {
    setLoad(true); setError('');
    try {
      const qs = new URLSearchParams({ limit: String(rpp), offset: String(page * rpp) });
      if (adjustmentId) qs.set('adjustmentId', String(adjustmentId));
      if (fWorker) qs.set('workerId', fWorker);
      if (fActor.trim()) qs.set('actor', fActor.trim());
      if (fAction) qs.set('action', fAction);
      if (fEvent)  qs.set('event', fEvent);
      if (fYear)   qs.set('year', String(fYear));
      if (from)    qs.set('from', from);
      if (to)      qs.set('to', to);

      const res  = await authFetch(`/api/leave-adjustments/audit?${qs}`);
      const json = await res.json().catch(() => ({}));
      if (res.status === 403) { setError('Nuk keni leje për të parë historikun e auditimit.'); setRows([]); setTotal(0); return; }
      if (!res.ok || json.success === false) { setError(json.error || `Nuk u morën të dhënat (HTTP ${res.status}).`); setRows([]); setTotal(0); return; }
      setRows(json.data?.rows || []);
      setTotal(json.data?.total || 0);
      setTz(json.data?.timezone || '');
      setTenant(json.data?.tenant || '');
    } catch {
      setError('Nuk u lidh dot me serverin. Kontrollo lidhjen dhe provo përsëri.');
      setRows([]); setTotal(0);
    } finally { setLoad(false); }
  }, [rpp, page, adjustmentId, fWorker, fActor, fAction, fEvent, fYear, from, to]);

  useEffect(() => { load(); }, [load]);

  /* The bytes never travel in the list response — this fetches them behind the
   * same permission the trail itself requires. */
  const openAudited = useCallback(async (adjustmentId) => {
    try {
      const res = await authFetch(`/api/leave-adjustments/${adjustmentId}/attachment`);
      if (!res.ok) { setError('Bashkëngjitja nuk u hap.'); return; }
      const url = URL.createObjectURL(await res.blob());
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { setError('Bashkëngjitja nuk u hap.'); }
  }, []);

  const thSx = { fontWeight: 700, fontSize: '0.68rem', py: 1, px: 1.25, color: '#6B7280', bgcolor: '#F9FAFB', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #E5E7EB', whiteSpace: 'nowrap' };
  const tdSx = { py: 0.75, px: 1.25, fontSize: '0.8rem', borderBottom: '1px solid #F3F4F6' };

  const hasFilters = useMemo(
    () => !!(fWorker || fActor || fAction || fEvent || fYear || from || to),
    [fWorker, fActor, fAction, fEvent, fYear, from, to]
  );

  return (
    <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, overflow: 'hidden' }}>
      {/* Header — the immutability claim is stated, not implied */}
      <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {/* Pandryshueshmëria është pohim, jo dekor — ndaj mban markën, jo një
            vjollcë të shpikur këtu. `onSoft` sepse `dark` nuk e kalon 4.5:1 mbi
            `soft` (shih theme.js). */}
        <LockRoundedIcon sx={{ fontSize: 17, color: 'primary.main' }} />
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'text.primary' }}>
          Historiku i auditimit
        </Typography>
        <Chip size="small" label="I pandryshueshëm"
          sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: 'primary.soft', color: 'primary.onSoft' }} />
        {tz && (
          <Tooltip title="Ruhet në UTC, shfaqet në orën e organizatës">
            <Chip size="small" label={tz}
              sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, bgcolor: '#F1F5F9', color: '#475569' }} />
          </Tooltip>
        )}
        {tenant && <Typography sx={{ fontSize: '0.7rem', color: '#9CA3AF' }}>· {tenant}</Typography>}
        <Tooltip title="Rifresko">
          <span style={{ marginLeft: 'auto' }}>
            <IconButton size="small" onClick={load} disabled={loading} sx={{ width: 30, height: 30 }}>
              <RefreshRoundedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Filters — date range, operator, employee, action */}
      {!adjustmentId && (
        <Box sx={{ px: 2, py: 1.25, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', borderBottom: '1px solid #F3F4F6' }}>
          <TextField size="small" type="date" label="Nga" InputLabelProps={{ shrink: true }}
            value={from} onChange={(e) => { setFrom(e.target.value); setPage(0); }} sx={{ width: 150 }} />
          <TextField size="small" type="date" label="Deri" InputLabelProps={{ shrink: true }}
            value={to} onChange={(e) => { setTo(e.target.value); setPage(0); }} sx={{ width: 150 }} />
          <WorkerPicker
            workers={workers} value={fWorker} emptyLabel="Të gjithë"
            onChange={(v) => { setFWorker(v); setPage(0); }}
            fullWidth={false} sx={{ width: 226, flexShrink: 0 }}
          />
          <TextField size="small" label="Operatori (email)" value={fActor}
            onChange={(e) => { setFActor(e.target.value); setPage(0); }} sx={{ width: 190 }} />
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Veprimi</InputLabel>
            <Select label="Veprimi" value={fAction} onChange={(e) => { setFAction(e.target.value); setPage(0); }}>
              <MenuItem value=""><em>Të gjitha</em></MenuItem>
              <MenuItem value="ADD">ADD</MenuItem>
              <MenuItem value="SUBTRACT">SUBTRACT</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Ngjarja</InputLabel>
            <Select label="Ngjarja" value={fEvent} onChange={(e) => { setFEvent(e.target.value); setPage(0); }}>
              <MenuItem value=""><em>Të gjitha</em></MenuItem>
              <MenuItem value="CREATE">Krijim</MenuItem>
              <MenuItem value="REVERSE">Kthim</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Viti</InputLabel>
            <Select label="Viti" value={fYear} onChange={(e) => { setFYear(e.target.value); setPage(0); }}>
              <MenuItem value=""><em>Të gjitha</em></MenuItem>
              {YEARS.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </Select>
          </FormControl>
          {hasFilters && (
            <Button size="small" onClick={() => {
              setFWorker(''); setFActor(''); setFAction(''); setFEvent(''); setFYear(''); setFrom(''); setTo(''); setPage(0);
            }} sx={{ textTransform: 'none' }}>Pastro</Button>
          )}
        </Box>
      )}

      {loading && <LinearProgress />}

      {!loading && error && (
        <Box sx={{ p: 2 }}>
          <Alert severity="error" sx={{ borderRadius: 2 }}
            action={<Button size="small" onClick={load} sx={{ textTransform: 'none', fontWeight: 700 }}>Provo përsëri</Button>}>
            {error}
          </Alert>
        </Box>
      )}

      {!error && (
        <TableContainer sx={{ maxHeight: '60vh' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {['', 'Data & ora', 'Ngjarja', 'Punonjësi', 'Viti', 'Lloji', 'Veprimi', 'Ditë', 'Para', 'Pas', 'Arsyeja', 'Referenca', 'Dëshmia', 'Operatori'].map((h, i) => (
                  <TableCell key={i} sx={thSx}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>{[...Array(14)].map((__, j) => <TableCell key={j} sx={tdSx}><Skeleton height={16} /></TableCell>)}</TableRow>
                ))
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={14} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" color="text.disabled">
                      {hasFilters ? 'Asnjë ngjarje nuk përputhet me filtrat' : 'Nuk ka ende ngjarje auditimi'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : rows.map((r) => {
                const open = openRow === r.Id;
                const isReverse = r.Event === 'REVERSE';
                return (
                  <React.Fragment key={r.Id}>
                    <TableRow hover>
                      <TableCell sx={{ ...tdSx, width: 34, px: 0.5 }}>
                        <IconButton size="small" onClick={() => setOpenRow(open ? null : r.Id)} sx={{ width: 24, height: 24 }}>
                          {open ? <KeyboardArrowUpIcon sx={{ fontSize: 16 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ ...tdSx, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                        {formatInTenantTz(r.CreatedAtUtc, tz)}
                      </TableCell>
                      <TableCell sx={tdSx}>
                        {/* Ngjarja mbetet Chip e jo `StatusChip`: CREATE/REVERSE
                            janë ngjarje të gjurmës, jo statuse të një kërkese, dhe
                            fjalori i statuseve nuk duhet të mbushet me to. Ngjyrat
                            vijnë nga paleta, jo nga hex-e të shkruara këtu. */}
                        <Chip size="small" icon={isReverse ? <UndoRoundedIcon sx={{ fontSize: '12px !important' }} /> : undefined}
                          label={isReverse ? 'Kthim' : 'Krijim'}
                          sx={{ height: 19, fontSize: '0.65rem', fontWeight: 700,
                                bgcolor: isReverse ? 'info.soft' : 'primary.soft',
                                color:   isReverse ? 'info.onSoft' : 'primary.onSoft' }} />
                      </TableCell>
                      <TableCell sx={tdSx}>{r.WorkerName || r.WorkerId}</TableCell>
                      <TableCell sx={tdSx}>{r.Year}</TableCell>
                      <TableCell sx={tdSx}>{r.LeaveTypeName || r.LeaveTypeId}</TableCell>
                      <TableCell sx={{ ...tdSx, fontWeight: 700, color: r.Action === 'SUBTRACT' ? '#B91C1C' : '#16A34A' }}>
                        {r.Action}
                      </TableCell>
                      <TableCell sx={{ ...tdSx, fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: r.Days < 0 ? '#B91C1C' : '#16A34A' }}>
                        {fmtSigned(r.Days)}
                      </TableCell>
                      <TableCell sx={{ ...tdSx, fontVariantNumeric: 'tabular-nums', color: '#6B7280' }}>{fmtDays(r.BalanceBefore)}</TableCell>
                      <TableCell sx={{ ...tdSx, fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtDays(r.BalanceAfter)}</TableCell>
                      <TableCell sx={{ ...tdSx, maxWidth: 200 }}>
                        <Tooltip title={r.Reason}><Typography sx={{ fontSize: '0.78rem' }} noWrap>{r.Reason}</Typography></Tooltip>
                      </TableCell>
                      <TableCell sx={tdSx}>{r.Reference || '—'}</TableCell>
                      {/* Dëshmia hapet nga vetë rreshti i historikut — ai që lexon
                          "pse lëvizi balanca" duhet ta shohë vendimin aty, jo ta
                          kërkojë te një ekran tjetër. Përmbajtja shërbehet nga
                          rruga e mbrojtur; këtu udhëton vetëm emri. */}
                      <TableCell sx={tdSx}>
                        {r.HasAttachment ? (
                          <Tooltip title={r.AttachmentName || 'Hap dëshminë'}>
                            <IconButton size="small" sx={{ width: 26, height: 26, color: '#0369A1' }}
                              onClick={() => openAudited(r.AdjustmentId)}>
                              <AttachFileRoundedIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        ) : <Typography sx={{ fontSize: '0.78rem', color: '#D1D5DB' }}>—</Typography>}
                      </TableCell>
                      <TableCell sx={tdSx}>
                        <Tooltip title={r.ActorEmail || ''}>
                          <Typography sx={{ fontSize: '0.78rem' }} noWrap>{r.ActorName || r.ActorEmail || '—'}</Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>

                    {/* Expanded: correlation id, tenant, links and metadata */}
                    <TableRow>
                      <TableCell colSpan={14} sx={{ p: 0, borderBottom: open ? '1px solid #F3F4F6' : 'none' }}>
                        <Collapse in={open} unmountOnExit>
                          <Box sx={{ px: 3, py: 1.5, bgcolor: '#FAFBFC' }}>
                            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                              <Meta label="Audit ID" value={r.Id} mono />
                              <Meta label="Rregullimi" value={r.AdjustmentId} mono />
                              <Meta label="Request ID" value={r.RequestId || '—'} mono />
                              <Meta label="Tenant" value={r.Tenant} />
                              <Meta label="Roli i operatorit" value={r.ActorRole || '—'} />
                              <Meta label="UTC" value={r.CreatedAtUtc} mono />
                              {r.ReversesAuditId && <Meta label="Kthen audit-in" value={r.ReversesAuditId} mono />}
                            </Stack>
                            {r.Metadata && Object.keys(r.Metadata).length > 0 && (
                              <Box sx={{ mt: 1.25 }}>
                                <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                                  Metadata
                                </Typography>
                                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                  {Object.entries(r.Metadata).map(([k, v]) => (
                                    <Chip key={k} size="small"
                                      label={`${k}: ${v === null ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v)}`}
                                      sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#F1F5F9', color: '#475569',
                                            fontFamily: 'ui-monospace, monospace' }} />
                                  ))}
                                </Stack>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!error && (
        <TablePagination
          component="div" count={total} page={page} rowsPerPage={rpp}
          rowsPerPageOptions={[25, 50, 100, 200]}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRpp(+e.target.value); setPage(0); }}
          labelRowsPerPage="Rreshta:"
          labelDisplayedRows={({ from: f, to: t, count }) => `${f}–${t} nga ${count}`}
          sx={{ borderTop: '1px solid #F3F4F6', fontSize: '0.75rem' }}
        />
      )}
    </Paper>
  );
}

function Meta({ label, value, mono }) {
  return (
    <Box>
      <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.76rem', color: '#374151', fontFamily: mono ? 'ui-monospace, monospace' : undefined }}>
        {String(value)}
      </Typography>
    </Box>
  );
}
